import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import MyLayout from './components/MyLayout';
import { fetchMyOrders, fetchMyQnA, type MyOrder } from '@/lib/myAccount';
import type { QnAItem } from '@/types/auth';

const statusColors: Record<string, string> = {
  pending: 'bg-[#F0EFED] text-[#8A8A8A]',
  paid: 'bg-[#7B1113]/10 text-[#7B1113]',
  shipped: 'bg-[#1E3A8A]/10 text-[#1E3A8A]',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export default function MyDashboardPage() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const [orders, setOrders] = useState<MyOrder[]>([]);
  const [qna, setQna] = useState<QnAItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    fetchMyOrders(currentUser.id).then((rows) => {
      setOrders(rows);
      setLoading(false);
    });
    fetchMyQnA(currentUser.id).then(setQna);
  }, [currentUser]);

  return (
    <MyLayout>
      <div className="flex flex-col gap-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-[#E8E8E6]">
            <p className="text-xs text-[#8A8A8A] uppercase tracking-wider mb-1">
              {t('my.orders.title', 'Orders')}
            </p>
            <p className="text-xl font-serif font-bold text-[#2A2A2A]">{orders.length}</p>
            <Link to={prefix + '/my/orders'} className="text-xs text-[#7B1113] hover:underline mt-2 inline-block">
              {t('common.viewDetails')}
            </Link>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#E8E8E6]">
            <p className="text-xs text-[#8A8A8A] uppercase tracking-wider mb-1">
              {t('my.qna.title', 'Q&A')}
            </p>
            <p className="text-xl font-serif font-bold text-[#2A2A2A]">{qna.length}</p>
            <Link to={prefix + '/my/qna'} className="text-xs text-[#7B1113] hover:underline mt-2 inline-block">
              {t('common.viewDetails')}
            </Link>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-2xl p-6 border border-[#E8E8E6]">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A]">
              {t('my.recentOrders', 'Recent Orders')}
            </h3>
            <Link to={prefix + '/my/orders'} className="text-xs text-[#7B1113] hover:underline">
              {t('common.seeAll')}
            </Link>
          </div>
          {loading ? (
            <p className="text-sm text-[#8A8A8A] py-4">{t('common.loading')}</p>
          ) : orders.length === 0 ? (
            <p className="text-sm text-[#8A8A8A] py-4">{t('my.orders.empty', 'No orders yet')}</p>
          ) : (
            orders.slice(0, 3).map((order) => (
              <div key={order.id} className="flex items-center justify-between py-4 border-b border-[#E8E8E6] last:border-0">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-[#2A2A2A]">{order.orderNo}</span>
                  <span className="text-xs text-[#8A8A8A]">{order.placedAt.slice(0, 10)}</span>
                  <span className="text-xs text-[#8A8A8A]">{order.items.map((i) => i.name).join(', ')}</span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${statusColors[order.status]}`}>
                    {order.status}
                  </span>
                  <span className="text-sm font-medium text-[#2A2A2A]">৳{order.totalBdt.toLocaleString()}</span>
                  <Link to={prefix + `/my/orders/${order.id}`} className="text-xs text-[#7B1113] hover:underline">
                    {t('my.orderDetail', 'Order Detail')}
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MyLayout>
  );
}
